import flatten from 'flat';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import isEmpty from 'lodash/isEmpty';
import { createValidator, isRequired, isRequiredIf, combineValidators, composeValidators } from 'revalidate';

import { EMPLOYEEGROUP_FIELD_CONFIG_CHOICES } from '../constants/config';
import moment from 'moment';
import { DATE_FORMAT_INPUT_DISPLAY } from '../constants/dateTimeFormats';
import { CONTACT_INFO_REQUIRED_ERROR } from '../constants/formSections';
const { REQUIRED } = EMPLOYEEGROUP_FIELD_CONFIG_CHOICES;

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
const ZIP_CODE_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

export function removeEmptyNestedObjects(obj) {
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
        let item = obj[keys[i]];
        if (isPlainObject(item)) {
            const rec_call = removeEmptyNestedObjects(obj[keys[i]]);
            if (isEmpty(rec_call)) {
                delete obj[keys[i]];
            }
        } else if (Array.isArray(item)) {
            item.forEach(arrItem => {
                removeEmptyNestedObjects(arrItem);
            });
            item = item.filter(arrItem => !isEmpty(arrItem));
            obj[keys[i]] = item;
            if (item.length === 0) {
                delete obj[keys[i]];
            }
        }
    }
    return obj;
}

export const arrayFriendlyIsRequiredIf = predicate => {
    return createValidator(
        message => (value, values) => {
            if (!predicate(values)) {
                return;
            }

            let isValid;

            if (Array.isArray(value)) {
                isValid = !!value.length;
            } else {
                isValid = !!value;
            }

            if (!isValid) {
                return message;
            }
        },
        fieldName => `${fieldName} is required`
    );
};

export const isValidEmail = createValidator(
    message => value => {
        if (value && !EMAIL_REGEX.test(value)) {
            return message;
        }
    },
    'Invalid email address'
);

export const isBrokerRequired = createValidator(
    message => (value, allValues) => {
        if (allValues.appointment && allValues.appointment.broker_booked) {
            return isRequired(message)(value, allValues);
        }
    },
    field => `${field}`
);

// Convert a data object into a list of valdiator keys
export function validatorFields(data) {
    return Object.keys(flatten(data)).map(field => {
        // convert "client.people.0.first_name" to "client.people[].first_name"
        return field.replace(/\.[0-9]+\./, '[].');
    });
}

export function isClientConditionallyRequired(otherFields = [], customMessage = '') {
    return createValidator(
        message => (value, otherValues) => {
            const fieldIsRequired = otherFields.every(key => {
                return !get(otherValues, key);
            });

            if (fieldIsRequired && !value) {
                return message;
            }
        },
        field => (customMessage ? customMessage : `${field} is required`)
    );
}

export const isClientContactOnFormOrRecord = createValidator(
    message => (value, otherValues) => {
        const contactFields = ['client.person.phone_1', 'client.person.email'];
        const noContactInfoOnFormOrRecord = contactFields.every(key => {
            // this is for v2 AppointmentScheduler, where we only show
            // this error if there is no contact info on the client record AND none provided in the form
            return !get(otherValues, key) && !get(otherValues, `_props.${key}`);
        });
        if (!value && noContactInfoOnFormOrRecord) {
            return message;
        }
    },
    CONTACT_INFO_REQUIRED_ERROR
);

export const isValidZipCode = createValidator(
    message => value => {
        if (value && !ZIP_CODE_REGEX.test(value)) {
            return message;
        }
    },
    'Invalid zip code'
);

export const isFutureDate = createValidator(
    message => value => {
        const currentDate = moment(moment().format(DATE_FORMAT_INPUT_DISPLAY), DATE_FORMAT_INPUT_DISPLAY);
        if (moment(value, DATE_FORMAT_INPUT_DISPLAY).isBefore(currentDate)) {
            return message;
        }
    },
    'Date should not be in the past'
);

export const isNotTooFarInFuture = maxDaysAhead =>
    createValidator(
        message => value => {
            if (!maxDaysAhead) return;

            const maxDate = moment().add(maxDaysAhead, 'days');

            if (moment(value, DATE_FORMAT_INPUT_DISPLAY).isAfter(maxDate)) {
                return message;
            }
        },
        'Date cannot be that far in the future'
    );

export const isValidDate = createValidator(
    message => value => {
        const testDateFormat = /^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d$/i.test(value);
        if (!testDateFormat || !moment(value, DATE_FORMAT_INPUT_DISPLAY).isValid()) {
            return message;
        }
    },
    'Invalid date'
);

export const isTourTypeFieldRequired = config => {
    return config['appointment.tour_type'] === REQUIRED;
};

/**
 fields is an option parameter that should be a list of fields
 fields must be in the key format expected by revalidate i.e. 'client.people[].email'
 in included, only the validators that match those fields will be included in
 the returned validator.
 **/
export const createClientValidator = (config, fields = null, settings = {}) => {
    const alwaysRequireEmail = get(settings, 'employeeGroup.always_require_email', true);
    const allValidators = {
        // format used for v1 widgets
        'client.people[].first_name': isRequired('First Name'),
        'client.people[].last_name': isRequired('Last Name'),
        // email always required (unlike on v2) because we need to send confirmation email
        'client.people[].email': composeValidators(
            isValidEmail(),
            isClientConditionallyRequired(['client.broker_email', 'client.broker_phone'])
        )('Email'),
        // format used for v2 widget
        'client.person.first_name': isRequired('First Name'),
        'client.person.last_name': isRequired('Last Name'),
        'client.person.email': composeValidators(isValidEmail(), isClientContactOnFormOrRecord())('Email'),
        'client.person.phone_1': isClientContactOnFormOrRecord('Phone Number'),
        'client.broker_email': composeValidators(isValidEmail(), isBrokerRequired('Broker Email'))('Email'),
        'client.amenities': arrayFriendlyIsRequiredIf(() => config['client.amenities'] === REQUIRED)('Amenities'),
        'client.neighborhoods': arrayFriendlyIsRequiredIf(() => config['client.neighborhoods'] === REQUIRED)(
            'Neighborhoods'
        ),
        'client.pets': arrayFriendlyIsRequiredIf(() => config['client.pets'] === REQUIRED)('Pets'),
        'client.client_referral': isRequiredIf(() => config['client.client_referral'] === REQUIRED)('Referral'),
        'client.layout': arrayFriendlyIsRequiredIf(() => config['client.layout'] === REQUIRED)('Layout'),
        'client.price_ceiling': isRequiredIf(() => config['client.price_ceiling'] === REQUIRED)('Price Max'),
        'client.price_floor': isRequiredIf(() => config['client.price_floor'] === REQUIRED)('Price Floor'),
        'client.notes': isRequiredIf(() => config['client.notes'] === REQUIRED)('Notes'),
        'client.move_in_date': isRequiredIf(() => config['client.move_in_date'] === REQUIRED)('Move in Date'),
        'client.broker_first_name': isBrokerRequired('Broker First Name'),
        'client.broker_last_name': isBrokerRequired('Broker Last Name'),
        'client.broker_company': isRequiredIf(
            allValues =>
                allValues.appointment &&
                allValues.appointment.broker_booked &&
                config['client.broker_company'] === REQUIRED
        )('Brokerage'),
        'client.broker_phone': isBrokerRequired('Broker Phone'),
        'client.other_occupants': isRequiredIf(() => config['client.other_occupants'] === REQUIRED)('Other Occupants'),
        'client.current_postal_code': composeValidators(
            isRequiredIf(() => config['client.current_postal_code'] === REQUIRED),
            isValidZipCode
        )('Zip Code'),
        time: isRequired('Time'),
        date: isRequired('Date'),
        'appointment.tour_type': isRequiredIf(() => isTourTypeFieldRequired(config))('Tour Type'),
        'appointment.platform_of_choice': isRequiredIf(() => config['appointment.platform_of_choice'] === REQUIRED)(
            'Platform of Choice'
        ),
        'appointment.platform_of_choice_details': isRequiredIf(
            () => config['appointment.platform_of_choice_details'] === REQUIRED
        )('Number, Email, or ID for video call'),
    };

    if (!alwaysRequireEmail) {
        allValidators['client.people[].email'] = composeValidators(
            isValidEmail(),
            isClientConditionallyRequired(['client.people[0].phone_1'], 'Email or Phone is required')
        )('Email');

        allValidators['client.people[].phone_1'] = isClientConditionallyRequired(
            ['client.people[0].email'],
            'Phone or Email is required'
        )('Phone Number');
    }

    let validators = {};
    if (fields) {
        fields.forEach(field => {
            // if a validator
            if (allValidators.hasOwnProperty(field)) {
                validators[field] = allValidators[field];
            }
        });
    } else {
        validators = allValidators;
    }

    return combineValidators(validators);
};
